import React, { useMemo } from 'react';

import { DataTable, CheckIcon, type TableColumn } from '@/src/ui';
import type { ClassMatrixDTO } from '../models/classMatrixDTOs';
import { ScoreCell, AnswerCell, HeaderCell } from './ClassMatrixCells';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

const coloredCellStyle = {
    padding: 0,
    height: 20,
    fontSize: '20px',
    fontWeight: 'bold',
    ReportCellStyle,
};

interface ClassMatrixReportDataTableProps {
    data: ClassMatrixDTO;
    onQuestionClick?: (questionId: number) => void;
}

export default function ClassMatrixReportDataTable({
    data,
    onQuestionClick,
}: ClassMatrixReportDataTableProps) {
    const questions = useMemo(() => data?.questions ?? [], [data?.questions]);
    const students = useMemo(() => data?.students ?? [], [data?.students]);
    const percents = useMemo(() => data?.questionPercents ?? [], [data?.questionPercents]);

    const classAvgPct = useMemo(
        () => Number(data?.classAveragePercent ?? 0),
        [data?.classAveragePercent]
    );

    const percentByQ = useMemo(() => {
        const m = new Map<number, number>();
        percents.forEach((p: any) => m.set(Number(p.questionId), Number(p.percentCorrect ?? 0)));
        return m;
    }, [percents]);

    const qMeta = useMemo(() => {
        return questions.map((q: any) => {
            const percent = Number(percentByQ.get(Number(q.questionId)) ?? 0);
            const letters = (q.correctChoicePositions ?? [])
                .map((x: number) => posToLetter(Number(x)))
                .join(',');
            const questionType = isChoiceType(q.type) ? letters || '—' : typeAbbrev(q.type);
            return { percent, questionType };
        });
    }, [questions, percentByQ]);

    const rows: GridRow[] = useMemo(() => {
        const qKeys = questions.map((q: any) => `q_${q.questionId}`);
        const rank = (s: 'correct' | 'wrong' | 'na') =>
            s === 'correct' ? 2 : s === 'wrong' ? 1 : 0;

        return students.map((stu: any) => {
            const status = stu.isIncomplete ? (
                'INC'
            ) : stu.needsHandscore ? (
                'HS'
            ) : (
                <CheckIcon color="green" style={{ width: '32px', height: '32px' }} />
            );

            const row: GridRow = {
                studentName: stu.studentName,
                percent: stu.percent == null ? null : Number(stu.percent),
                reportingBand: stu.reportingBand ?? '',
                status,
            };

            qKeys.forEach((key, i) => {
                const q = questions[i];
                const ans = (stu.answers ?? []).find(
                    (a: any) => Number(a.questionId) === Number(q.questionId)
                );

                if (!ans || !ans.isAnswered) {
                    (row as any)[key] = { text: '–', status: 'na' as const };
                    (row as any)[`${key}_sort`] = `0|`;
                    return;
                }

                const positions = answerToPositions(
                    ans.submittedAnswer,
                    (q.choiceOptions ?? []) as string[]
                );
                const letters = positions.map((p) => posToLetter(Number(p))).join(',');

                let st: 'correct' | 'wrong' | 'na' = 'na';
                if (typeof ans.isCorrect === 'boolean') st = ans.isCorrect ? 'correct' : 'wrong';

                (row as any)[key] = { text: letters || '–', status: st };
                (row as any)[`${key}_sort`] = `${rank(st)}|${letters}`;
            });

            return row;
        });
    }, [questions, students]);

    const columns = useMemo<HeaderTableColumn[]>(() => {
        const cols: HeaderTableColumn[] = [
            {
                dataKey: 'studentName',
                header: 'Student',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: {
                    whiteSpace: 'nowrap',
                    ...ReportCellStyle,
                },
                formatter: (row: GridRow) => row.studentName,
            },
            {
                dataKey: 'percent',
                header: <HeaderCell top="Score" row2={`${classAvgPct}%`} />,
                align: 'center',
                allowSort: true,
                sortMethod: (a: any, b: any, sortOrder) => {
                    const swap = sortOrder === 'desc' ? 1 : -1;
                    if (a == null) return 1;
                    if (b == null) return -1;
                    return (a - b) * swap;
                },
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: coloredCellStyle,
                formatter: (row: GridRow) => (
                    <ScoreCell value={row.percent} band={row.reportingBand} />
                ),
            },
            {
                dataKey: 'status',
                header: 'Status',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: GridRow) => (
                    <span
                        className="font-semibold"
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        {row.status ?? (
                            <CheckIcon color="green" style={{ width: '32px', height: '32px' }} />
                        )}
                    </span>
                ),
            },
        ];

        questions.forEach((q: any, idx: number) => {
            const key = `q_${q.questionId}`;
            const meta = qMeta[idx];

            cols.push({
                dataKey: `${key}_sort`,
                header: (
                    <HeaderCell
                        top={
                            <a
                                href="#"
                                title={`Open details for Q${idx + 1}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onQuestionClick?.(Number(q.questionId));
                                }}
                                className="!text-[var(--pm-secondary)] cursor-pointer"
                            >
                                Q{idx + 1}
                            </a>
                        }
                        row2={`${meta?.percent ?? 0}%`}
                        row3={meta?.questionType ?? '—'}
                    />
                ),
                align: 'center',
                allowSort: true,
                sortMethod: (a: any, b: any, sortOrder) => {
                    const [aRank] = String(a ?? '').split('|');
                    const [bRank] = String(b ?? '').split('|');
                    if (aRank == '0') return 1;
                    if (bRank == '0') return -1;

                    const swap = sortOrder === 'desc' ? 1 : -1;
                    if (aRank !== bRank) {
                        return (Number(aRank) - Number(bRank)) * swap;
                    } else {
                        return (a - b) * swap;
                    }
                },
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: coloredCellStyle,
                formatter: (row: GridRow) => {
                    const cell = (row as any)[key];
                    if (!cell || typeof cell === 'string') {
                        const txt = (cell ?? '–') as string;
                        return <AnswerCell text={txt} status="na" />;
                    }
                    return <AnswerCell text={cell.text} status={cell.status} />;
                },
            });
        });

        return cols;
    }, [questions, qMeta, classAvgPct, onQuestionClick]);

    return (
        <DataTable
            data={rows}
            columns={columns as unknown as TableColumn[]}
            tableName="ClassMatrixReport"
            rowsPerPageInitial={50}
            enableExportCSV
            alternatingRowColor
            rowSx={() => ({ minHeight: 60 })}
            defaultSortColumn="studentName"
            defaultSortDirection="asc"
        />
    );
}

// Helpers

const posToLetter = (p: number) => String.fromCharCode('A'.charCodeAt(0) + (p - 1));

const answerToPositions = (submitted: string | null | undefined, options: string[]) => {
    const parts = (submitted ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);

    const positions: number[] = [];
    parts.forEach((val) => {
        if (/^[A-Za-z]$/.test(val)) {
            positions.push(val.toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0) + 1);
            return;
        }

        const n = Number(val);
        if (Number.isFinite(n) && n >= 1) {
            positions.push(n);
            return;
        }

        const idx = options.indexOf(val);
        if (idx >= 0) positions.push(idx + 1);
    });

    return positions;
};

type GridRow = any & {
    studentName: string;
    percent?: number | null | string;
    reportingBand?: string;
    status?: string;
};

type HeaderTableColumn = Omit<TableColumn, 'header'> & { header: React.ReactNode };

const CHOICE_TYPES = new Set(['multi choice', 'multi select']);

const isChoiceType = (t: string) =>
    CHOICE_TYPES.has(
        String(t ?? '')
            .trim()
            .toLowerCase()
    );

const typeAbbrev = (t: string): string => {
    switch (t) {
        case 'Matrix':
            return 'MA';
        case 'Drag & Drop':
            return 'DD';
        case 'Graphing':
            return 'GR';
        case 'Dynamic':
            return 'DY';
        case 'Open Text':
            return 'OT';
        case 'Stimulus':
            return 'ST';
        case 'Segment':
            return 'SG';
        default:
            return '—';
    }
};
